import { useEffect, useRef, useState } from 'react'
import type { FC } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Popover } from 'antd'
import { DisconnectOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'

import { E_ShareholderStatuses } from '@/enums'
import { routes } from '@/router/routes'
import { rqKeys } from '@/react-query-keys'
import { formatMoney, isCordovaApp } from '@/helpers'
import { clearIDBPersistedQueryClient } from '@/helpers/query-persister'
import { apiRequest } from '@/helpers/request'
import { clearAuthTokens } from '@/helpers/request'
import { broadcastLogoutSync } from '@/helpers/request'
import { useOnClickOutside, useOnlineStatus } from '@/helpers/hooks'
import { useAppStore } from '@/store'
import { Button, Flex, Modal, Skeleton } from '@/components'
import { MessagesNotifier, useMessagesStore } from '@/pages/messages'
import { DocumentsDrawer } from './components/documents-drawer'
import { usePWAInstall } from '@/b-components/pwa'
import { profile } from '@/profile'
import type { T_UniRes } from '@/types'
import type { T_UserBalanceRes } from '@/types/responses'
import iconLogoSvg from '@/img/logo.svg'
import iconUser from '@/img/icon-user.svg'
import {
  SC_TopBarWrapper,
  SC_LeftSide,
  SC_RightSide,
  SC_TopMenuWrapper,
  SC_DropdownMenu,
  SC_OfflineIcon,
} from './styled'


export const TopBar: FC = () => {
  const navigate = useNavigate()
  const isOnline = useOnlineStatus()
  const queryClient = useQueryClient()
  const mainAccountLabel = 'Основной счёт'

  const isAuthorized = useAppStore((s) => s.isAuthorized)
  const setToUnauthorized = useAppStore((s) => s.setToUnauthorized)
  const userData = useAppStore((s) => s.userData)
  const setMainAccountBalanceFromApi = useAppStore((s) => s.setMainAccountBalanceFromApi)
  const resetMessages = useMessagesStore((s) => s.reset)
  // const hasStructureMembers = useAppStore((s) => s.hasStructureMembers) // structure отключено

  // PWA установка
  const {
    isInstallAvailable,
    supportsPromptAPI,
    installInstructions,
    promptInstall,
  } = usePWAInstall()

  const [ isContextMenuVisible, setIsContextMenuVisible ] = useState<boolean>(false)
  const showContextMenu = () => setIsContextMenuVisible(true)
  const hideContextMenu = () => setIsContextMenuVisible(false)

  // Документы

  const [ isDocumentsDrawerVisible, setIsDocumentsDrawerVisible ] = useState<boolean>(false)
  const showDocumentsDrawer = () => setIsDocumentsDrawerVisible(true)
  const hideDocumentsDrawer = () => setIsDocumentsDrawerVisible(false)


  const [ isLogoutModalVisible, setIsLogoutModalVisible ] = useState<boolean>(false)
  const showLogoutModal = () => {
    setIsLogoutModalVisible(true)
    hideContextMenu()
  }


  const {
    data: balanceRes,
    isLoading: isBalanceLoading,
  } = useQuery<T_UniRes<T_UserBalanceRes> & { _requestRevision?: number }>({
    queryKey: [ rqKeys.userBalance ],
    queryFn: async () => {
      // 1. Запоминаем ревизию ДО начала запроса
      const revisionAtStart = useAppStore.getState().balanceRevision

      const res = await apiRequest<T_UniRes<T_UserBalanceRes>>({
        method: 'money/account/balance',
        param: {},
      })

      // 2. Пробрасываем её вместе с данными
      return { ...res, _requestRevision: revisionAtStart }
    },
  })

  const balanceList = balanceRes?.data?.list
  const sharesAccount = Array.isArray(balanceList)
    ? balanceList.find((item) => item.amount.format.includes(' п.'))
    : undefined
  const sharesCount = Number(sharesAccount?.amount?.value) || 0

  useEffect(() => {
    // Проверка на успешность запроса и наличие данных (Data Race Risk fix)
    if (!balanceRes || balanceRes.code !== 'ok' || !balanceRes.data || !Array.isArray(balanceRes.data.list)) return

    const mainAccount = balanceRes.data.list.find((item) => item.currency === 1)

    if (mainAccount) {
      const newBalance = Number(mainAccount.amount.value) || 0
      // Используем безопасный метод обновления с проверкой ревизии
      if (typeof balanceRes._requestRevision === 'number') {
        setMainAccountBalanceFromApi(newBalance, balanceRes._requestRevision)
      }
    }
  }, [ balanceRes, userData.mainAccount ])

  const {
    mutateAsync: logoutMutation,
  } = useMutation({
    mutationFn: () => apiRequest({ method: 'client/visitor/logout' }),
  })

  const handleInstallApp = async () => {
    if (supportsPromptAPI) {
      await promptInstall()
    } else {
      alert(installInstructions)
    }
    hideContextMenu()
  }

  const goToPremium = () => {
    if (!isOnline) {
      toast.error('Нет подключения к интернету. Подключитесь, пожалуйста, к сети и попробуйте снова.')
      return
    }

    const premiumUrl = 'https://era.vision/office/dashboard'

    if (isCordovaApp) {
      // @ts-expect-error cordova global
      if (typeof cordova !== 'undefined' && cordova.InAppBrowser) {
        // @ts-expect-error cordova global
        cordova.InAppBrowser.open(premiumUrl, '_blank')
      } else {
        window.open(premiumUrl, '_blank')
      }
    } else {
      window.open(premiumUrl, '_blank')
    }

    hideContextMenu()
  }

  const goToCabinet = () => {
    if (!isOnline) {
      toast.error('Нет подключения к интернету. Подключитесь, пожалуйста, к сети и попробуйте снова.')
      return
    }

    navigate(routes.cabinet)
    hideContextMenu()
  }

  const goToProfile = () => {
    navigate(routes.profile)
    hideContextMenu()
  }

  const canGoToPremium = (
    userData?.shareholderStatusId !== E_ShareholderStatuses.None
    && userData?.shareholderStatusId !== E_ShareholderStatuses.Want
    && userData?.shareholderStatusId !== E_ShareholderStatuses.Waiting
  ) || (userData?.sourceCompanyId !== 0 && profile.functionalAll)

  // const goToStructure = () => {
  //   navigate(routes.structure)
  //   hideContextMenu()
  // }

  // Делаем функцию асинхронной
  const logout = async () => {
    resetMessages()
    setToUnauthorized()
    navigate(routes.auth)

    // Рассылаем logout-сигнал в другие вкладки/контексты до сетевого запроса.
    broadcastLogoutSync()

    // 1) Очистка токенов (refresh + in-memory new API token)
    // Удаление токена до запроса logoutMutation() не ломает сам запрос логаута (он всё равно уйдёт с кукой).
    // Зато это удаление гарантирует, что если во время выполнения запроса логаута
    //  (который может длиться 1-2 секунды) какой-то другой компонент (например, CheckAuth или фоновый запрос)
    //  попробует проверить авторизацию, он не найдет токен и не сможет "случайно" восстановить сессию обратно.
    clearAuthTokens()

    // 2) Вызов выхода на сервере (сессия на куках)
    try {
      await logoutMutation()
    } catch (e) {
      console.warn('[logout] mutation failed', e)
    }

    // 3) Очистка кэша React Query
    queryClient.clear()

    // 4) Очистка сохранённого кэша React Query (IndexedDB) по мере возможности
    void clearIDBPersistedQueryClient().catch((e) => {
      console.warn('[logout] failed to clear persisted cache', e)
    })

    hideContextMenu()
    setIsLogoutModalVisible(false)
  }

  const dropdownRef = useRef(null)
  useOnClickOutside(dropdownRef, () => hideContextMenu())

  if (!isAuthorized) return null


  return <>
    <SC_TopBarWrapper>
      <SC_LeftSide onClick={() => navigate(routes.home)}>
        <img src={profile.isLogoEye ? '/vsem/assets/images/logo.svg' : iconLogoSvg} />

        {window.location.hostname === 'stage-app.era.vision'
          ? (
            <span
              style={{
                display: 'inline-block',
                background: '#f00',
                padding: '0.5rem',
              }}
            >
              DEV
            </span>
          )
          : null
        }
      </SC_LeftSide>

      <SC_RightSide>
        {!isOnline && (
          <Popover
            content={(
              <div style={{ maxWidth: 300 }}>
                Сеть сейчас недоступна, поэтому показано последнее доступное состояние приложения. Всё обновится, как только вы вновь подключитесь к интернету.
              </div>
            )}
            trigger='click'
            placement='bottomRight'
          >
            <SC_OfflineIcon>
              <DisconnectOutlined />&nbsp;Не в сети
            </SC_OfflineIcon>
          </Popover>
        )}

        {sharesCount >= 1 ? (
          <Popover
            content={(
              <div>
                {isBalanceLoading && (
                  <Skeleton count={2} />
                )}

                {balanceRes?.data?.list?.map((item, i) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: i === (balanceRes?.data?.list?.length || 0) - 1 ? 0 : 12,
                    }}
                  >
                    <div style={{ marginRight: 16 }}>
                      {item.amount.format.includes(' п.') ? 'Паевой счёт' : mainAccountLabel}
                    </div>

                    <div style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {item.amount.format}
                    </div>
                  </div>
                ))}
              </div>
            )}
            trigger='click'
            placement='bottomRight'
          >
            <span style={{ cursor: 'pointer' }}>
              {formatMoney(userData.mainAccount)}
            </span>
          </Popover>
        ) : (
          <span style={{ cursor: 'default' }}>
            {formatMoney(userData.mainAccount)}
          </span>
        )}

        <MessagesNotifier />

        <SC_TopMenuWrapper>
          <Button
            type='text'
            icon={<img src={iconUser} />}
            onClick={() => showContextMenu()}
          />

          {isContextMenuVisible && (
            <SC_DropdownMenu ref={dropdownRef}>
              <div onClick={goToProfile}>
                Профиль
              </div>

              <div onClick={goToCabinet}>
                Кабинет
              </div>

              {/* structure — временно отключено
              {hasStructureMembers && (
                <div onClick={goToStructure}>
                  Моё сообщество
                </div>
              )}
              */}

              {profile.isEraPremium && (
                <div
                  onClick={canGoToPremium ? goToPremium : undefined}
                  title={canGoToPremium ? undefined : 'Переход в ЭРА.Премиум доступен только для пайщиков. Станьте, пожалуйста, пайщиком.'}
                  style={canGoToPremium ? undefined : { opacity: 0.5, cursor: 'default' }}
                >
                  ЭРА.Премиум
                </div>
              )}

              <div onClick={showDocumentsDrawer}>
                Документы
              </div>

              {isInstallAvailable && (
                <div onClick={handleInstallApp}>
                  Установить как приложение
                </div>
              )}

              <div onClick={showLogoutModal}>
                Выйти из аккаунта
              </div>
            </SC_DropdownMenu>
          )}
        </SC_TopMenuWrapper>
      </SC_RightSide>
    </SC_TopBarWrapper>

    {isLogoutModalVisible && (
      <Modal
        open={isLogoutModalVisible}
        onCancel={() => setIsLogoutModalVisible(false)}
        centered
        footer={(
          <Flex gap={16} justify='space-evenly' style={{ width: '100%' }}>
            <Button
              onClick={() => setIsLogoutModalVisible(false)}
            >
              Нет
            </Button>

            <Button
              type='primary'
              onClick={logout}
            >
              Да
            </Button>
          </Flex>
        )}
      >
        <h3>Вы действительно хотите выйти из аккаунта?</h3>
      </Modal>
    )}

    {isDocumentsDrawerVisible && (
      <DocumentsDrawer
        hideDocumentsDrawer={hideDocumentsDrawer}
      />
    )}
  </>
}
